/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ChartType } from "~/generated/graphql";
import { DateRange, EstatePeriod, Granularity, Period } from "~/components";
import { useQueryState } from "~/hooks";
import {
  EstateGroupBy,
  EstateRecordsGroupingChart,
  EstateRecordsGroupingTable,
  EstateTotals,
  getEndDate,
  getStartDate,
  useEstateRecordsGroupByChartJob,
} from "~/views/estate/components/Charts";
import {
  APPLICATIONS_SORT_OPTIONS,
  EstateChartsQueryState,
} from "./components";
import { useEstateRecordsCostPerProvider } from "./components/Charts/hooks/useEstateRecordsCostPerProvider";
import { TotalCostTable } from "./components/Charts/TotalCostTable";
import { mapEstatePeriodToPeriod } from "./helpers";

type Props = {
  period: EstatePeriod;
  granularity: Granularity;
  range: DateRange | undefined;
};

export function EstateRecordsProviderView({
  period,
  granularity,
  range,
}: Props) {
  const {
    state: { periodStart, periodEnd },
  } = useQueryState<EstateChartsQueryState>();

  const startDate =
    periodStart ?? getStartDate(EstatePeriod.CURRENT_MONTH, undefined);
  const endDate =
    periodEnd ?? getEndDate(EstatePeriod.CURRENT_MONTH, undefined);

  const {
    isSubmitLoading,
    isChartLoading,
    chartData,
    isTableLoading,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.Provider,
    startDate,
    endDate,
  });

  const mappedPeriod = mapEstatePeriodToPeriod(period);

  const {
    costPerProviderChartData,
    costPerProviderTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
  } = useEstateRecordsCostPerProvider({
    chartData,
    tableData,
    granularity,
    period: mappedPeriod,
    startDate,
    endDate,
  });

  const firstColumnWidth = mappedPeriod === Period.MONTH ? "30%" : undefined;

  return (
    <>
      <EstateRecordsGroupingChart
        data={costPerProviderChartData}
        granularity={granularity}
        loading={isSubmitLoading || isChartLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
      />
      <EstateTotals
        period={period}
        range={range}
        chartType={EstateGroupBy.PROVIDER}
      >
        <TotalCostTable
          count={tableData?.chartJobTable?.data?.count}
          isLoading={isSubmitLoading || isTableLoading}
          chartType={EstateGroupBy.PROVIDER}
          totalTop={extraDetails?.totalTop ?? "-"}
          totalOther={extraDetails?.totalOther ?? "-"}
        />
      </EstateTotals>
      <EstateRecordsGroupingTable
        loading={isSubmitLoading || isTableLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={APPLICATIONS_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={costPerProviderTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </>
  );
}
